import React from 'react';
import { Box, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { isIOS, iosBrowser } from '@app/shared/utils/env';

// Inline 3-step pipeline (Settings → Privacy & Security → Allow {browser})
// shown inside an existing error container when geolocation was denied on
// iOS. Self-gating: returns null on non-iOS or when the error isn't a
// permission denial, so callers can drop it in unconditionally.

const browserLabel: string = (() => {
  switch (iosBrowser) {
    case 'chrome':  return 'Chrome';
    case 'firefox': return 'Firefox';
    case 'edge':    return 'Edge';
    default:        return 'Safari';
  }
})();

const tileSx = (bg: string) => ({
  width: 22,
  height: 22,
  borderRadius: '5px',
  background: bg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 1px 1.5px rgba(0,0,0,0.18)',
});

const labelSx = { fontSize: 12, color: '#555' } as const;
const chevronStyle = { fontSize: 10, color: '#9aa0a6' };

export const IOSLocationHelpInline: React.FC = () => {
  const { t, i18n } = useTranslation();
  const geolocation = useRecoilValue(atom_geolocationState);

  if (!isIOS || geolocation.error?.code !== 1) return null;

  const isRTL = i18n.language === 'he';
  const chevronCls = isRTL ? 'fa-solid fa-chevron-left' : 'fa-solid fa-chevron-right';

  return (
    <Box
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        mt: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        rowGap: 0.75,
        columnGap: 0.5,
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={tileSx('linear-gradient(180deg,#c8c8c8,#7d7d7d)')}>
          <i className="fa-solid fa-gear" style={{ color: '#fff', fontSize: 11 }} />
        </Box>
        <Typography sx={labelSx}>{t('directions.ios_help.step_settings')}</Typography>
      </Box>

      <i className={chevronCls} style={chevronStyle} />

      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={tileSx('#1f72ff')}>
          <i className="fa-solid fa-hand" style={{ color: '#fff', fontSize: 11 }} />
        </Box>
        <Typography sx={labelSx}>{t('directions.ios_help.step_privacy')}</Typography>
      </Box>

      <i className={chevronCls} style={chevronStyle} />

      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={tileSx('linear-gradient(180deg,#9aa1ad,#5b6371)')}>
          <i className="fa-solid fa-paper-plane" style={{ color: '#fff', fontSize: 11 }} />
        </Box>
        <Typography sx={labelSx}>
          {t('directions.ios_help.step_allow_for', { browser: browserLabel })}
        </Typography>
      </Box>
    </Box>
  );
};

export default IOSLocationHelpInline;
